import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { usePathname } from 'expo-router';
import { useMemo, useState } from 'react';
import {
    ActivityIndicator,
    Platform,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';

import { useSession } from '../../lib/SessionContext';
import colors from '../../lib/colors';
import { canAccessMainApp } from '../../utils/authHelpers';
import LanguageDropdown from './LanguageDropdown';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';
import {
    canActivatePersistedProductLanguage,
    getI18nBuildTarget,
    getSelectableProductLanguages,
    matchLiveMessageLocaleTag,
    type ProductionLocale,
} from '../../lib/i18n/locales';
import {
    findPublicWebSurface,
    isStaticPublicWebExport,
    publicLocaleSwitchUrl,
    stripManagedPublicLocalePrefix,
} from '../../lib/i18n/publicWeb';

type LanguageSelectorVariant = 'header' | 'mobile' | 'settings';

export default function LanguageSelector({
    variant = 'header',
    onNavigate,
}: {
    variant?: LanguageSelectorVariant;
    onNavigate?: () => void;
}) {
    const { i18n } = useLingui();
    const pathname = usePathname();
    const { messageLocale, setSessionLanguageTag } = useAppLocale();
    const { session, setPreferredLanguageTag } = useSession();
    const languages = useMemo(() => getSelectableProductLanguages(), []);
    const [pendingLanguage, setPendingLanguage] = useState<ProductionLocale | null>(null);
    const [saveFailed, setSaveFailed] = useState(false);
    const logicalPathname = stripManagedPublicLocalePrefix(pathname);
    const publicSurface = findPublicWebSurface(logicalPathname);
    const localizedSurface = publicSurface?.localeEligibility === 'localized';
    // Locale-prefixed URLs such as /es/... exist only in the static export. The
    // dev server serves the unprefixed app, so it switches the session language.
    const usesPublicUrl = localizedSurface && isStaticPublicWebExport();
    const selectedLanguage: ProductionLocale = messageLocale === 'es' ? 'es' : 'en';
    const label = i18n._(msg({
        id: 'account.settingsscreen.preferences',
        message: 'Preferences',
    }));

    if (languages.length < 2 || (publicSurface && !localizedSurface)) {
        return null;
    }

    const selectLanguage = async (targetLanguage: ProductionLocale) => {
        if (pendingLanguage || targetLanguage === selectedLanguage) return;
        setSaveFailed(false);
        onNavigate?.();

        if (usesPublicUrl) {
            if (Platform.OS !== 'web' || typeof window === 'undefined') {
                setSaveFailed(true);
                return;
            }
            const targetUrl = publicLocaleSwitchUrl(window.location.href, targetLanguage);
            if (!targetUrl) {
                setSaveFailed(true);
                return;
            }
            window.location.assign(targetUrl);
            return;
        }

        setPendingLanguage(targetLanguage);
        try {
            const targetIsLive = matchLiveMessageLocaleTag(targetLanguage) === targetLanguage;
            // Signed-out visitors have no profile to save to.
            const shouldPersist = getI18nBuildTarget() === 'production'
                && canActivatePersistedProductLanguage()
                && targetIsLive
                && canAccessMainApp(session);
            if (!shouldPersist) {
                setSaveFailed(!setSessionLanguageTag(targetLanguage));
                return;
            }
            const result = await setPreferredLanguageTag(targetLanguage);
            setSaveFailed(!result.ok);
        } finally {
            setPendingLanguage(null);
        }
    };

    return (
        <View
            style={[
                styles.wrapper,
                variant === 'mobile' && styles.wrapperMobile,
                variant === 'settings' && styles.wrapperSettings,
            ]}
            accessibilityLabel={label}
            testID={`language-selector-${variant}`}
        >
            {variant === 'settings' ? <Text style={styles.settingsLabel}>{label}</Text> : null}
            {Platform.OS === 'web' ? (
                <LanguageDropdown languages={languages} selected={selectedLanguage}
                    label={label} busy={Boolean(pendingLanguage)} variant={variant}
                    onSelect={(tag) => { void selectLanguage(tag as ProductionLocale); }} />
            ) : <View style={styles.options}>
                {languages.map((language) => {
                    const selected = selectedLanguage === language.tag;
                    const pending = pendingLanguage === language.tag;
                    return (
                        <TouchableOpacity
                            key={language.tag}
                            style={[
                                styles.option,
                                variant === 'mobile' && styles.optionMobile,
                                variant === 'settings' && styles.optionSettings,
                                selected && styles.optionSelected,
                            ]}
                            accessibilityRole="button"
                            accessibilityLabel={language.nativeName}
                            accessibilityState={{ selected, busy: pending }}
                            disabled={Boolean(pendingLanguage)}
                            onPress={() => {
                                void selectLanguage(language.tag);
                            }}
                            testID={`language-selector-${variant}-${language.tag}`}
                        >
                            {pending ? (
                                <ActivityIndicator size="small" color={colors.primaryGreenDark} />
                            ) : (
                                <Text style={[
                                    styles.optionText,
                                    selected && styles.optionTextSelected,
                                ]}>
                                    {language.nativeName}
                                </Text>
                            )}
                        </TouchableOpacity>
                    );
                })}
            </View>}
            {saveFailed ? (
                <Text
                    style={styles.errorText}
                    accessibilityLiveRegion="polite"
                >
                    {i18n._(msg({
                        id: 'account.error.saveFailed',
                        message: 'Failed to save settings. Please try again.',
                    }))}
                </Text>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        alignItems: 'center',
        marginHorizontal: 4,
        zIndex: 100,
    },
    wrapperMobile: {
        alignItems: 'flex-start',
        marginHorizontal: 24,
        marginVertical: 10,
    },
    wrapperSettings: {
        alignItems: 'flex-start',
        width: '100%',
    },
    settingsLabel: {
        color: colors.text900,
        fontSize: 14,
        fontWeight: '600',
        marginBottom: 8,
    },
    options: {
        borderColor: colors.border,
        borderRadius: 9,
        borderWidth: 1,
        flexDirection: 'row',
        overflow: 'hidden',
    },
    option: {
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 36,
        minWidth: 72,
        paddingHorizontal: 10,
        paddingVertical: 7,
        ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
    },
    optionMobile: {
        flex: 1,
        minHeight: 44,
    },
    optionSettings: {
        minHeight: 44,
        minWidth: 112,
    },
    optionSelected: {
        backgroundColor: colors.primaryGreenLight,
    },
    optionText: {
        color: colors.primaryGreenDark,
        fontSize: 14,
        fontWeight: '500',
    },
    optionTextSelected: {
        fontWeight: '700',
    },
    errorText: {
        color: colors.errorRed,
        fontSize: 12,
        marginTop: 6,
        maxWidth: 260,
    },
});
